import { useState } from 'react';
import { Profile } from '../lib/types';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Search, Eye, Trash2, CheckCircle, XCircle, Users, Loader2,
  MapPin, Briefcase, GraduationCap, Phone, Mail, Shield,
} from 'lucide-react';
import { toast } from 'sonner';

interface UserManagementProps {
  profiles: Profile[];
  onUpdateProfile: (profile: Profile) => Promise<void>;
  onDeleteProfile: (profileId: string) => Promise<void>;
  onVerifyProfile: (
    profileId: string,
    type: 'phone' | 'email' | 'driverLicense',
    approved: boolean,
  ) => Promise<void>;
}

const PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23f3f4f6"/%3E%3Ccircle cx="50" cy="38" r="18" fill="%23d1d5db"/%3E%3Cellipse cx="50" cy="85" rx="30" ry="20" fill="%23d1d5db"/%3E%3C/svg%3E';

function Avatar({ name, picture, size = 'md' }: { name: string; picture?: string; size?: 'sm' | 'md' | 'lg' }) {
  const cls = { sm: 'size-9 text-sm', md: 'size-11 text-base', lg: 'size-20 text-2xl' }[size];
  if (picture) {
    return (
      <img src={picture} alt={name}
        className={`${cls} rounded-xl object-cover shrink-0`}
        onError={e => { (e.target as HTMLImageElement).src = PLACEHOLDER; }} />
    );
  }
  return (
    <div className={`${cls} rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold shrink-0`}>
      {name?.[0]?.toUpperCase() || '?'}
    </div>
  );
}

function VerBadge({ status }: { status?: string }) {
  const s = status || 'pending';
  const cfg = {
    verified: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-600',
    pending:  'bg-amber-100 text-amber-700',
  }[s] || 'bg-amber-100 text-amber-700';
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg}`}>
      {s.charAt(0).toUpperCase() + s.slice(1)}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  if (type === 'paid') return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gradient-to-r from-pink-500 to-violet-500 text-white">Premium</span>;
  if (type === 'agent') return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Agent</span>;
  return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Free</span>;
}

export function UserManagement({ profiles, onUpdateProfile, onDeleteProfile, onVerifyProfile }: UserManagementProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selected, setSelected] = useState<Profile | null>(null);
  const [profileToDelete, setProfileToDelete] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [verifyingKey, setVerifyingKey] = useState<string | null>(null);
  const [updatingType, setUpdatingType] = useState(false);

  const filtered = profiles.filter(p => {
    const hay = [p.name, p.email, p.id, (p as any)._id].filter(Boolean).join(' ').toLowerCase();
    return (!searchQuery || hay.includes(searchQuery.toLowerCase())) &&
           (typeFilter === 'all' || p.userType === typeFilter);
  });

  const counts = {
    all: profiles.length,
    free: profiles.filter(p => p.userType === 'free').length,
    paid: profiles.filter(p => p.userType === 'paid').length,
    agent: profiles.filter(p => p.userType === 'agent').length,
  };

  const handleDelete = async () => {
    if (!profileToDelete) return;
    setDeletingId(profileToDelete);
    try {
      await onDeleteProfile(profileToDelete);
      toast.success('User deleted');
      setProfileToDelete(null);
      if (selected && ((selected as any)._id === profileToDelete || selected.id === profileToDelete)) {
        setSelected(null);
      }
    } finally { setDeletingId(null); }
  };

  const handleChangeType = async (newType: 'free' | 'paid' | 'agent') => {
    if (!selected || selected.userType === newType) return;
    setUpdatingType(true);
    try {
      await onUpdateProfile({ ...selected, userType: newType });
      toast.success(`Account type changed to ${newType === 'paid' ? 'Premium' : newType}`);
      setSelected(prev => (prev ? { ...prev, userType: newType } : prev));
    } catch {
      // onUpdateProfile (handleUpdateProfile) already surfaces the error toast
    } finally {
      setUpdatingType(false);
    }
  };

  const handleVerify = async (type: 'phone' | 'email' | 'driverLicense', approved: boolean) => {
    if (!selected) return;
    const pid = (selected as any)._id || selected.id;
    const key = `${pid}|${type}`;
    setVerifyingKey(key);
    try {
      await onVerifyProfile(pid, type, approved);
      toast.success(`${type} ${approved ? 'verified' : 'rejected'}`);
      setSelected(prev => prev ? { ...prev, verification: { ...prev.verification, [type]: approved ? 'verified' : 'rejected' } } : prev);
    } finally { setVerifyingKey(null); }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="size-5 text-violet-600" /> User Management
          </h2>
          <p className="text-sm text-gray-400 mt-0.5">{profiles.length} total users</p>
        </div>

        {/* Type filter pills */}
        <div className="flex gap-1.5 flex-wrap">
          {(['all', 'free', 'paid', 'agent'] as const).map(t => (
            <button key={t} onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                typeFilter === t
                  ? 'bg-violet-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}>
              {t === 'all' ? 'All' : t === 'paid' ? 'Premium' : t.charAt(0).toUpperCase() + t.slice(1)}
              <span className="ml-1 opacity-70">({counts[t]})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
        <input
          placeholder="Search by name, email or ID…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 h-10 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-violet-400 bg-white"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <XCircle className="size-4" />
          </button>
        )}
      </div>

      {/* User cards grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Users className="size-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium text-gray-500">{profiles.length === 0 ? 'No users loaded' : 'No users match your search'}</p>
          {profiles.length > 0 && <p className="text-sm mt-1">Try adjusting your filters</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(profile => {
            const pid = (profile as any)._id || profile.id;
            const emailStatus = profile.verification?.email;
            const phoneStatus = profile.verification?.phone;
            return (
              <div key={pid} className="bg-white rounded-2xl border border-gray-100 p-4 hover:shadow-md transition-shadow">
                {/* Top row */}
                <div className="flex items-start gap-3 mb-3">
                  <Avatar name={profile.name} picture={profile.profilePicture} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900 text-sm truncate">{profile.name}</p>
                      <TypeBadge type={profile.userType} />
                    </div>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{profile.email}</p>
                    {profile.location && (
                      <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                        <MapPin className="size-3" />{profile.location}
                      </p>
                    )}
                  </div>
                </div>

                {/* Details row */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
                  {profile.age && (
                    <span className="text-xs text-gray-500">{profile.age} yrs</span>
                  )}
                  {profile.job && (
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Briefcase className="size-3" />{profile.job}
                    </span>
                  )}
                  {profile.qualification && (
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <GraduationCap className="size-3" />{profile.qualification}
                    </span>
                  )}
                </div>

                {/* Verification row */}
                <div className="space-y-1.5 mb-4 py-2.5 px-3 bg-gray-50 rounded-xl text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Mail className="size-3 text-gray-400 shrink-0" />
                      <span className="text-gray-600 truncate">{profile.email || '—'}</span>
                    </div>
                    <VerBadge status={emailStatus} />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Phone className="size-3 text-gray-400 shrink-0" />
                      <span className="text-gray-600 truncate">{profile.phone || '—'}</span>
                    </div>
                    <VerBadge status={phoneStatus} />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelected(profile)}
                    className="flex-1 h-8 text-xs font-semibold bg-violet-50 hover:bg-violet-100 text-violet-700 rounded-lg flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <Eye className="size-3.5" /> View Details
                  </button>
                  <button
                    onClick={() => setProfileToDelete(pid)}
                    disabled={deletingId === pid}
                    className="h-8 w-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-500 transition-colors disabled:opacity-50"
                  >
                    {deletingId === pid ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-gray-400 text-right">Showing {filtered.length} of {profiles.length} users</p>

      {/* Profile Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={v => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
          {selected && (() => {
            const pid = (selected as any)._id || selected.id;
            return (
              <>
                {/* Cover */}
                <div className="relative h-28 bg-gradient-to-br from-violet-500 to-purple-700 rounded-t-xl">
                  <div className="absolute -bottom-8 left-5">
                    <Avatar name={selected.name} picture={selected.profilePicture} size="lg" />
                  </div>
                  <div className="absolute top-3 right-3">
                    <TypeBadge type={selected.userType} />
                  </div>
                </div>

                <div className="px-5 pt-12 pb-5 space-y-5">
                  <DialogHeader>
                    <DialogTitle className="text-lg">
                      {selected.name}
                      {selected.age ? <span className="text-gray-400 font-normal text-sm ml-1">, {selected.age}</span> : null}
                    </DialogTitle>
                    <p className="text-sm text-gray-500">{selected.email}</p>
                    {selected.location && (
                      <p className="text-xs text-gray-400 flex items-center gap-1"><MapPin className="size-3" />{selected.location}</p>
                    )}
                    <p className="text-[10px] text-gray-300 font-mono">{pid}</p>
                  </DialogHeader>

                  {/* About */}
                  {selected.about && (
                    <p className="text-sm text-gray-600 bg-gray-50 rounded-xl p-3 leading-relaxed">{selected.about}</p>
                  )}

                  {/* Photos */}
                  {(() => {
                    const photos = [selected.profilePicture, ...((selected as any).photos || [])].filter(Boolean) as string[];
                    if (photos.length <= 1) return null;
                    return (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Photos ({photos.length})</p>
                        <div className="grid grid-cols-3 gap-2">
                          {photos.map((photo, i) => (
                            <div key={i} className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                              <img src={photo} alt="" className="w-full h-full object-cover"
                                onError={e => { (e.target as HTMLImageElement).src = PLACEHOLDER; }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Info grid */}
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Height', value: selected.height },
                      { label: 'Weight', value: selected.weight },
                      { label: 'Body Type', value: selected.bodyType },
                      { label: 'Complexion', value: selected.complexion },
                      { label: 'Job', value: selected.job },
                      { label: 'Company', value: selected.company },
                      { label: 'Qualification', value: selected.qualification },
                      { label: 'College', value: selected.college },
                    ].filter(f => f.value).map(f => (
                      <div key={f.label} className="bg-gray-50 rounded-xl p-3">
                        <p className="text-[10px] text-gray-400 uppercase tracking-wide">{f.label}</p>
                        <p className="text-sm font-medium text-gray-800 mt-0.5">{f.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Interests */}
                  {selected.interests?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Interests</p>
                      <div className="flex flex-wrap gap-1.5">
                        {selected.interests.map(i => (
                          <span key={i} className="text-xs bg-violet-50 text-violet-600 px-2.5 py-1 rounded-full border border-violet-100">{i}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Verification */}
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Verification</p>
                    <div className="space-y-2">
                      {([
                        { type: 'email' as const, label: 'Email', icon: <Mail className="size-4" />, value: selected.email },
                        { type: 'phone' as const, label: 'Phone', icon: <Phone className="size-4" />, value: selected.phone },
                        { type: 'driverLicense' as const, label: 'Government ID', icon: <Shield className="size-4" />, value: undefined },
                      ]).map(({ type, label, icon, value }) => {
                        const status = selected.verification?.[type] ?? 'pending';
                        const key = `${pid}|${type}`;
                        const busy = verifyingKey === key;
                        return (
                          <div key={type} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-xl">
                            <div className="flex items-center gap-2.5 min-w-0 flex-1">
                              <span className="text-gray-400 shrink-0">{icon}</span>
                              <div className="min-w-0">
                                <p className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</p>
                                {value && <p className="text-xs font-medium text-gray-700 truncate">{value}</p>}
                              </div>
                              <VerBadge status={status} />
                            </div>
                            {status !== 'verified' && (
                              <div className="flex gap-1.5 shrink-0">
                                <button
                                  disabled={busy}
                                  onClick={() => handleVerify(type, true)}
                                  className="h-7 px-2.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-1 disabled:opacity-50 transition-colors"
                                >
                                  {busy ? <Loader2 className="size-3 animate-spin" /> : <CheckCircle className="size-3" />}
                                  Approve
                                </button>
                                {status !== 'rejected' && (
                                  <button
                                    disabled={busy}
                                    onClick={() => handleVerify(type, false)}
                                    className="h-7 px-2.5 text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1 disabled:opacity-50 transition-colors"
                                  >
                                    <XCircle className="size-3" /> Reject
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Account type */}
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Account Type</p>
                    <div className="flex gap-2">
                      {([
                        { value: 'free' as const, label: 'Free' },
                        { value: 'paid' as const, label: 'Premium' },
                        { value: 'agent' as const, label: 'Agent' },
                      ]).map(opt => {
                        const active = selected.userType === opt.value;
                        return (
                          <button
                            key={opt.value}
                            disabled={updatingType || active}
                            onClick={() => handleChangeType(opt.value)}
                            className={`flex-1 h-9 text-xs font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-colors disabled:cursor-default ${
                              active
                                ? 'bg-violet-600 text-white shadow-sm'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50'
                            }`}
                          >
                            {updatingType && !active ? <Loader2 className="size-3.5 animate-spin" /> : null}
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-2">Changing the type updates the user's membership tier immediately.</p>
                  </div>

                  {/* Footer actions */}
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => { setProfileToDelete(pid); setSelected(null); }}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border border-red-200 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      <Trash2 className="size-4" /> Delete User
                    </button>
                    <button
                      onClick={() => setSelected(null)}
                      className="flex-1 py-2 text-sm font-semibold bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!profileToDelete} onOpenChange={() => setProfileToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the user profile and all associated data. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingId}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" disabled={!!deletingId}>
              {deletingId ? <><Loader2 className="size-4 mr-1 animate-spin" />Deleting…</> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
